import React, {useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import { Button, Form, Card, Row, Col, Spinner } from 'react-bootstrap';
import { PersonCircle, KeyFill, BoxArrowRight } from 'react-bootstrap-icons';
import dbService from '../../../services/dbService';
import PageContainer from '../../layout/PageContainer';

const ProfilePage = () => {
    const { user, logout } = useAuth(); // Get current user and logout function
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setIsSaving(true);

        // 1. Validation
        if (newPassword !== confirmPassword) {
            toast.error("New passwords do not match.");
            setIsSaving(false);
            return;
        }
        if (newPassword.length < 8) {
            toast.error("New password must be at least 8 characters long.");
            setIsSaving(false);
            return;
        }

        // 2. Verify current password
        if (currentPassword !== user.password) {
            toast.error("Incorrect current password.");
            setIsSaving(false);
            return;
        }
        
        // 3. Update the password in the database
        const updatedUser = { ...user, password: newPassword };
        try {
            await dbService.update('users', updatedUser);
            await dbService.addLog({ user: user.name, action: 'Password Changed', details: 'User changed their own password.' });
            
            // 4. Reset form and notify user
            toast.success("Password changed successfully! Please log in again for security.");
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            
            // 5. Log the user out for security
            setTimeout(() => {
                logout();
            }, 2000);

        } catch (error) {
            toast.error("Failed to update password. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    if (!user) {
        return <Spinner animation="border" />; // Should not happen on a private route, but good practice
    }

    return (
        <PageContainer title='My Profile'>
            <Row>
                <Col md={6}>
                    <Card className="mb-4">
                        <Card.Header as="h5" className="d-flex align-items-center">
                           <PersonCircle className="me-2" /> User Details
                        </Card.Header>
                        <Card.Body>
                            <p><strong>Full Name:</strong> {user.name}</p>
                            <p><strong>Email Address:</strong> {user.email}</p>
                            <p><strong>Role:</strong> {user.role}</p>
                            <p><strong>Team / Group:</strong> {user.team}</p>
                        </Card.Body>
                    </Card>

                    <Card>
                        <Card.Header as="h5" className="d-flex align-items-center">
                            <BoxArrowRight className="me-2" /> Logout
                        </Card.Header>
                        <Card.Body>
                            <p>Click the button below to securely end your session.</p>
                            <Button variant="danger" onClick={logout}>
                                Logout of System
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={6}>
                    <Card>
                         <Card.Header as="h5" className="d-flex align-items-center">
                            <KeyFill className="me-2" /> Change Password
                        </Card.Header>
                        <Card.Body>
                            <Form onSubmit={handleChangePassword}>
                                <Form.Group className="mb-3" controlId="currentPassword">
                                    <Form.Label>Current Password</Form.Label>
                                    <Form.Control 
                                        type="password" 
                                        placeholder="Enter your current password" 
                                        value={currentPassword}
                                        onChange={e => setCurrentPassword(e.target.value)}
                                        required
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3" controlId="newPassword">
                                    <Form.Label>New Password</Form.Label>
                                    <Form.Control 
                                        type="password" 
                                        placeholder="Minimum 8 characters"
                                        value={newPassword}
                                        onChange={e => setNewPassword(e.target.value)}
                                        required
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3" controlId="confirmPassword">
                                    <Form.Label>Confirm New Password</Form.Label>
                                    <Form.Control 
                                        type="password" 
                                        placeholder="Re-enter your new password"
                                        value={confirmPassword}
                                        onChange={e => setConfirmPassword(e.target.value)}
                                        required
                                    />
                                </Form.Group>
                                <Button variant="primary" type="submit" disabled={isSaving}>
                                    {isSaving ? <Spinner as="span" size="sm" /> : 'Update Password'}
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageContainer>
    );
};

export default ProfilePage;